import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Coins, Gem, ShieldOff, Package, CheckCircle2, Star, Gift } from "lucide-react";
import MockAdDialog from "../components/MockAdDialog";
import LoadingState from "../components/LoadingState";
import { isAndroidNative, buyOnAndroid } from "../lib/nativeBilling";
import { useTranslation } from "react-i18next";

export default function Store() {
  const { t } = useTranslation();
  const { profile, setProfile } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [adOpen, setAdOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bonusClaimed, setBonusClaimed] = useState(false);
  const todayISO = new Date().toISOString().slice(0, 10);
  const dailyBonusClaimed = bonusClaimed || profile?.premium_bonus_last_claim === todayISO;

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get("/store");
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || t("store.couldnt_load"));
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const buy = async (sku) => {
    if (busy) return;
    setBusy(true);
    try {
      if (isAndroidNative()) {
        const result = await buyOnAndroid(sku);
        if (result?.profile) setProfile(result.profile);
        toast.success(
          result?.already_granted
            ? t("store.toast_restored")
            : t("store.toast_purchase_ok")
        );
        if (result?.first_purchase_bonus > 0) {
          toast.success(t("store.toast_first_bonus", { n: result.first_purchase_bonus }));
        }
        await load();
      } else {
        const { data } = await api.post("/payments/checkout/session", {
          sku,
          origin_url: window.location.origin,
        });
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || t("store.toast_checkout_failed"));
    } finally {
      setBusy(false);
    }
  };

  const buyBoosterPack = async (pack_id) => {
    try {
      const { data } = await api.post("/boosters/buy", { pack_id });
      setProfile(data);
      toast.success(t("store.toast_pack_added"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("store.toast_cannot_buy"));
    }
  };

  const claimBonus = async () => {
    try {
      const { data } = await api.post("/premium/daily-bonus");
      setProfile(data.profile);
      setBonusClaimed(true);
      toast.success(t("store.toast_daily_bonus", { n: data.coins_granted }));
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (e?.response?.status === 400 || /already/i.test(detail)) setBonusClaimed(true);
      toast.error(detail || t("dashboard.toast_cannot_claim"));
    }
  };

  const watchCoinsAd = async () => {
    try {
      const { data } = await api.post("/coins/reward-ad");
      setProfile(data);
      toast.success(t("store.toast_coins_added"));
    } catch {
      toast.error(t("store.toast_could_not_grant"));
    }
  };

  if (!data || error) return <LoadingState error={error} onRetry={load} rows={6} testid="store" />;

  const boosterLabel = (b) => {
    if (b === "hint") return t("play.hint");
    if (b === "shuffle") return t("play.shuffle");
    if (b === "undo") return t("play.undo");
    return b;
  };

  return (
    <div className="space-y-10">
      <div>
        <p className="text-sm uppercase tracking-[0.25em] text-[#8AA48F] mb-2">{t("store.store")}</p>
        <h1 style={{ fontFamily: "Outfit, sans-serif" }} className="text-4xl sm:text-5xl font-light tracking-tight">
          {t("store.headline")}
        </h1>
        <p className="text-[#2C332A]/70 mt-3 max-w-2xl">
          {t("store.intro_part1")}
          <strong>{t("store.intro_part2")}</strong>
          {t("store.intro_part3")}
        </p>
      </div>

      {/* Premium */}
      <section>
        <h2 style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl font-light mb-4">
          {t("store.membership")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {data.premium.map((p) => {
            const owned =
              (p.sku === "remove_ads" && profile?.no_ads) ||
              (p.sku === "pro_fitness_pack" && profile?.premium);
            return (
              <div
                key={p.sku}
                data-testid={`premium-${p.sku}`}
                className={`p-8 rounded-3xl border ${owned ? "border-[#5F7161] bg-[#F1EEE6]" : "border-[#E6E2D8] bg-white"}`}
              >
                <div className="flex items-start justify-between mb-3">
                  {p.sku === "pro_fitness_pack" ? (
                    <Gem className="text-[#5F7161]" strokeWidth={1.5} />
                  ) : (
                    <ShieldOff className="text-[#5F7161]" strokeWidth={1.5} />
                  )}
                </div>
                <h3 style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl font-medium mb-1">
                  {p.label}
                </h3>
                <p className="text-sm text-[#2C332A]/60 mb-6">
                  {p.sku === "pro_fitness_pack"
                    ? t("store.pro_pack_desc")
                    : t("store.remove_ads_desc")}
                </p>
                <div className="flex items-end justify-between">
                  <p className="text-3xl font-light">£{p.price_usd.toFixed(2)}</p>
                  <Button
                    data-testid={`buy-${p.sku}`}
                    disabled={owned}
                    onClick={() => buy(p.sku)}
                    className={`rounded-full ${
                      owned
                        ? "bg-[#5F7161]/15 text-[#5F7161] disabled:opacity-100 disabled:bg-[#5F7161]/15 disabled:text-[#5F7161]"
                        : "bg-[#5F7161] hover:bg-[#4D5D4F] text-white"
                    }`}
                  >
                    {owned ? (
                      <>
                        <CheckCircle2 size={16} strokeWidth={2.5} className="mr-1.5" />
                        {t("store.owned")}
                      </>
                    ) : (
                      t("store.buy_now")
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        {profile?.premium && (
          <div
            className={`mt-4 p-4 rounded-2xl flex items-center justify-between gap-3 border transition-colors ${
              dailyBonusClaimed
                ? "bg-[#EFF1EC] border-[#E6E2D8]"
                : "bg-gradient-to-br from-[#FBF1E4] to-[#F6E8D2] border-[#E8A365]/50"
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={`shrink-0 h-11 w-11 rounded-full grid place-items-center ${
                  dailyBonusClaimed ? "bg-[#D9DED4]" : "bg-[#E8A365]/20"
                }`}
              >
                {dailyBonusClaimed ? (
                  <CheckCircle2 className="text-[#8A9A86]" strokeWidth={2} size={22} />
                ) : (
                  <Gift className="text-[#E8A365]" strokeWidth={1.75} size={22} />
                )}
              </div>
              <div className="min-w-0">
                <p className={`font-semibold ${dailyBonusClaimed ? "text-[#8A9A86]" : "text-[#2C332A]"}`}>
                  {t("store.pro_daily_bonus")}
                </p>
                <p className="text-sm text-[#2C332A]/60 truncate">{t("store.pro_daily_bonus_desc")}</p>
              </div>
            </div>
            <Button
              data-testid="claim-daily-bonus-btn"
              onClick={claimBonus}
              disabled={dailyBonusClaimed}
              className={`rounded-full shrink-0 ${
                dailyBonusClaimed
                  ? "bg-[#D9DED4] text-[#8A9A86] disabled:opacity-100"
                  : "bg-[#E8A365] hover:bg-[#Dc9250] text-white shadow-sm"
              }`}
            >
              {dailyBonusClaimed ? (
                <>
                  <CheckCircle2 size={16} strokeWidth={2.5} className="mr-1.5" />
                  {t("store.claimed", "Claimed")}
                </>
              ) : (
                t("store.claim_todays_bonus")
              )}
            </Button>
          </div>
        )}
      </section>

      {/* Coin packs */}
      <section>
        <h2 style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl font-light mb-4">
          {t("store.coin_packs")}
        </h2>
        {data.first_purchase_bonus_coins > 0 && !data.state?.first_purchase_done && (
          <div
            data-testid="first-purchase-bonus-banner"
            className="mb-4 flex items-center gap-3 p-4 rounded-2xl bg-[#FBF1E4] border border-[#E8A365]/40"
          >
            <Gem className="text-[#E8A365] shrink-0" strokeWidth={1.75} />
            <p className="text-sm text-[#7A5A2E]">
              {t("store.first_bonus_banner", { n: data.first_purchase_bonus_coins })}
            </p>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {data.coin_packs.map((p) => {
            const popular = p.pack_id === "coins_medium";
            return (
            <div
              key={p.pack_id}
              data-testid={`coinpack-${p.pack_id}`}
              className={`relative p-6 rounded-3xl bg-white border ${
                popular ? "border-[#E8A365] ring-2 ring-[#E8A365]/30 shadow-md" : "border-[#E6E2D8]"
              }`}
            >
              {popular && (
                <Badge
                  data-testid="most-popular-badge"
                  className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E8A365] hover:bg-[#E8A365] text-white gap-1 shadow-sm whitespace-nowrap"
                >
                  <Star size={12} strokeWidth={2.5} fill="currentColor" />
                  {t("store.most_popular")}
                </Badge>
              )}
              <Coins className="text-[#E8A365] mb-2" strokeWidth={1.5} />
              <p className="text-sm uppercase tracking-[0.2em] text-[#8AA48F]">{p.label}</p>
              <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-3xl font-light mt-1">
                {p.coins.toLocaleString()}
              </p>
              <p className="text-sm text-[#2C332A]/60">{t("store.coins_unit")}</p>
              <p className="text-xs text-[#2C332A]/55 mt-3 leading-snug">
                {t(`store.coinpack_${p.pack_id}_desc`)}
              </p>
              <Button
                data-testid={`buy-${p.pack_id}`}
                onClick={() => buy(p.pack_id)}
                className={`mt-4 w-full rounded-full text-white ${
                  popular ? "bg-[#E8A365] hover:bg-[#D8915A]" : "bg-[#5F7161] hover:bg-[#4D5D4F]"
                }`}
              >
                £{p.price_usd.toFixed(2)}
              </Button>
            </div>
            );
          })}
        </div>
        <div className="mt-4 p-4 rounded-2xl bg-gradient-to-br from-[#FBF1E4] to-[#F6E8D2] border border-[#E8A365]/50">
          <div className="flex items-center gap-3">
            <div className="shrink-0 h-11 w-11 rounded-full grid place-items-center bg-[#E8A365]/20">
              <Coins className="text-[#E8A365]" strokeWidth={1.75} size={22} />
            </div>
            <p className="text-sm text-[#2C332A]/80 font-medium leading-snug">
              {t("store.low_on_coins")}
            </p>
          </div>
          <Button
            data-testid="watch-coins-ad-btn"
            className="mt-3 w-full rounded-full bg-[#E8A365] hover:bg-[#Dc9250] text-white shadow-sm"
            onClick={() => setAdOpen(true)}
          >
            {t("store.watch_ad")}
          </Button>
        </div>
      </section>

      {/* Booster packs */}
      <section>
        <h2 style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl font-light mb-4">
          {t("store.booster_packs")}
        </h2>
        <p className="text-sm text-[#2C332A]/60 mb-4">
          {t("store.booster_packs_intro", {
            hint: profile?.boosters?.hint ?? 0,
            shuffle: profile?.boosters?.shuffle ?? 0,
            undo: profile?.boosters?.undo ?? 0,
          })}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {data.booster_packs.map((p) => (
            <div key={p.pack_id} data-testid={`boosterpack-${p.pack_id}`} className="p-6 rounded-3xl bg-white border border-[#E6E2D8]">
              <Package className="text-[#D0A98F] mb-2" strokeWidth={1.5} />
              <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-xl font-medium">
                {boosterLabel(p.booster)} × {p.count}
              </p>
              <p className="text-sm text-[#2C332A]/60 mt-1">{t("store.cost_coins", { n: p.cost_coins })}</p>
              <p className="text-xs text-[#2C332A]/55 mt-2 leading-snug">
                {t(`store.booster_${p.booster}_desc`)}
              </p>
              <Button
                data-testid={`buy-booster-${p.pack_id}`}
                disabled={(profile?.coins ?? 0) < p.cost_coins}
                onClick={() => buyBoosterPack(p.pack_id)}
                className="mt-4 w-full rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white disabled:bg-[#E6E2D8] disabled:text-[#6E756B]"
              >
                {t("store.buy")}
              </Button>
            </div>
          ))}
        </div>
      </section>

      <MockAdDialog
        open={adOpen}
        onOpenChange={setAdOpen}
        title={t("store.earn_25")}
        description={t("store.earn_25_desc")}
        rewardLabel={t("store.earn_25_reward")}
        rewardType="coins"
        onReward={watchCoinsAd}
      />
    </div>
  );
}
